import { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '../ui';
import { useUser } from '../../context/UserContext';
import {
  enablePush,
  getPushSubscription,
  needsHomeScreenInstall,
  pushSupported,
} from '../../lib/push';

const DISMISS_KEY = 'push-prompt-dismissed';

/**
 * Soft pre-prompt for push notifications. Browsers punish (or, on iOS,
 * reject) cold permission prompts, so this small card asks first — its
 * Enable button is the user gesture that triggers the real prompt.
 * Shows once per device: enabling, dismissing, or denying all end it.
 */
export default function PushPrompt() {
  const { user } = useUser();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return undefined;
    if (!pushSupported()) return undefined;
    if (needsHomeScreenInstall()) return undefined;
    if (Notification.permission !== 'default') return undefined;
    try {
      if (localStorage.getItem(DISMISS_KEY)) return undefined;
    } catch {
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const existing = await getPushSubscription().catch(() => null);
      if (!cancelled && !existing) setVisible(true);
    }, 4000);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [user]);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // storage unavailable — the card just returns next session
    }
    setVisible(false);
  };

  const handleEnable = async () => {
    if (!user || busy) return;
    setBusy(true);
    try {
      await enablePush(user.id);
    } catch (err) {
      console.error('Push enable from prompt failed:', err);
    } finally {
      setBusy(false);
      dismiss();
    }
  };

  if (!visible || !user) return null;

  return (
    <div
      className="ck-menu-pop ck-above-nav fixed z-40 left-4 md:left-auto right-4 md:right-6"
      style={{
        maxWidth: '320px',
        background: 'var(--surface-card)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-pop)',
        padding: '16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <Bell size={16} style={{ color: 'var(--accent-text)', flexShrink: 0, marginTop: '2px' }} />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              color: 'var(--text-body)',
            }}
          >
            Get alerts on this device
          </div>
          <p style={{ margin: '4px 0 12px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.45 }}>
            A banner and a sound for your alerts, even when Cockpit is closed. Pick what dings you in Settings → Notifications.
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button size="sm" onClick={handleEnable} disabled={busy}>
              {busy ? 'Enabling…' : 'Enable'}
            </Button>
            <Button size="sm" variant="ghost" onClick={dismiss} disabled={busy}>
              Not now
            </Button>
          </div>
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={dismiss}
          className="pressable"
          style={{
            background: 'transparent',
            border: 0,
            padding: '2px',
            color: 'var(--text-faint)',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
